/**
 * "+100 if min avg views 10K → 5K · +50 if max followers 250K → 500K · +160 if both"
 *
 * Creators this search already fetched and checked, that failed only a
 * numeric threshold. Each option can be added to the folder straight away —
 * no new search, no extra cost — or applied to the form for the next search.
 * Handles are one click away: "+100" is a number to be talked into; five
 * handles you can open is a number you can check.
 */
import { useState, type JSX } from 'react';
import { Check, ChevronRight, Loader2, SlidersHorizontal, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PlatformIcon } from '@/components/PlatformIcon';
import { cn } from '@/lib/utils';
import { profileUrl, toPlatform } from '../lib/platform';
import type { NearMisses, NearMissOption, SearchSchema } from '../api/types';
import { useIncludeCreators } from './hooks';
import { filterPhrase, formatMissedValue, nearMissLabel, sortNearMissOptions } from './insights';
import { compactNumber } from './params';

const PREVIEW = 5;
const num = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? compactNumber(n) : '—');

export function NearMissPanel({
  nearMisses,
  schema,
  jobId,
  folderPublicId,
  canInclude,
  onApply,
}: {
  nearMisses: NearMisses | null;
  schema: SearchSchema | null;
  jobId: string | null;
  folderPublicId: string;
  /** False for the legacy `widen` output — that server can't add without re-searching. */
  canInclude: boolean;
  /** Put the option's thresholds into the form for the next search. Absent = hide the button. */
  onApply?: (option: NearMissOption) => void;
}): JSX.Element | null {
  const [openId, setOpenId] = useState<string | null>(null);
  const [added, setAdded] = useState<Record<string, number>>({});
  const include = useIncludeCreators(jobId, folderPublicId);

  const options = nearMisses ? sortNearMissOptions(nearMisses.options.filter((o) => o.count > 0)) : [];
  if (options.length === 0) return null;

  function addOption(o: NearMissOption) {
    const usernames = o.creators.map((c) => c.username);
    if (usernames.length === 0) return;
    include.mutate(
      { usernames, source: 'nearMiss' },
      {
        onSuccess: (res) => {
          const n = typeof res?.added === 'number' ? res.added : usernames.length;
          setAdded((prev) => ({ ...prev, [o.id]: n }));
          toast.success(`Added ${n} creator${n === 1 ? '' : 's'} — no extra search cost`);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not add them'),
      },
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/30">
      <div className="flex items-start gap-2 px-3 pt-2.5">
        <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">Close calls</p>
          <p className="text-xs text-muted-foreground">
            Already found and checked by this search; they missed one threshold.
            {canInclude ? ' Add them now at no extra search cost, or loosen the form for next time.' : ' Loosen the form to include them next time.'}
          </p>
        </div>
      </div>
      <ul className="space-y-1 p-2">
        {options.map((o) => {
          const label = nearMissLabel(schema, o);
          const isOpen = openId === o.id;
          const addedN = added[o.id];
          const listed = o.creators.length;
          return (
            <li key={o.id} className="rounded-lg border border-border bg-card">
              <div className="flex flex-wrap items-center gap-2 px-2.5 py-1.5">
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : o.id)}
                  aria-expanded={isOpen}
                  className="flex min-w-[14rem] flex-1 items-center gap-1.5 text-left text-sm"
                >
                  <ChevronRight className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150', isOpen && 'rotate-90')} />
                  <span className="font-mono font-semibold tabular-nums text-brand">{label.count}</span>
                  <span className="truncate">{label.text}</span>
                  {label.detail && <span className="hidden truncate text-xs text-muted-foreground md:inline">({label.detail})</span>}
                </button>
                <div className="ml-auto flex shrink-0 items-center gap-1">
                  {onApply && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onApply(o)} title="Set these thresholds for the next search">
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      Apply to form
                    </Button>
                  )}
                  {canInclude &&
                    listed > 0 &&
                    (addedN !== undefined ? (
                      <span className="inline-flex items-center gap-1 px-2 text-xs font-medium text-success-strong">
                        <Check className="h-3.5 w-3.5" /> Added {addedN}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={include.isPending || !jobId}
                        onClick={() => addOption(o)}
                        title="Adds them from what this search already fetched — no extra search cost"
                      >
                        {include.isPending && include.variables?.usernames.length === listed && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Add these {listed.toLocaleString()}
                      </Button>
                    ))}
                </div>
              </div>
              {isOpen && <OptionCreators option={o} schema={schema} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function OptionCreators({ option, schema }: { option: NearMissOption; schema: SearchSchema | null }): JSX.Element {
  const [all, setAll] = useState(false);
  const shown = all ? option.creators : option.creators.slice(0, PREVIEW);
  const missedOn = option.changes[0]?.filter;
  const detail = nearMissLabel(schema, option).detail;
  return (
    <div className="border-t border-border px-2.5 py-1.5">
      {detail && <p className="pb-1 text-xs text-muted-foreground md:hidden">{detail}</p>}
      {shown.length === 0 ? (
        <p className="py-1 text-xs text-muted-foreground">The server didn't list these creators.</p>
      ) : (
        <ul className={cn('divide-y divide-border', all && 'max-h-72 overflow-y-auto pr-1')}>
          {shown.map((c) => (
            <li key={c.username} className="flex items-center gap-2 py-1">
              <PlatformIcon platform={toPlatform(c.platform)} size={14} className="shrink-0 text-muted-foreground" />
              <a
                href={profileUrl(c.platform ?? undefined, c.username)}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-sm hover:underline"
              >
                @{c.username}
              </a>
              {missedOn && (
                <span className="shrink-0 text-xs tabular-nums text-pending-strong" title={`Their ${filterPhrase(schema, missedOn).replace(/^(min|max)\s+/, '')}`}>
                  {formatMissedValue(schema, option, c.value)}
                </span>
              )}
              <span className="hidden shrink-0 text-xs tabular-nums text-muted-foreground sm:inline">
                {num(c.followers)} followers · {num(c.avgViews)} avg views
              </span>
            </li>
          ))}
        </ul>
      )}
      {!all && option.creators.length > PREVIEW && (
        <Button type="button" variant="link" size="sm" className="h-auto px-0" onClick={() => setAll(true)}>
          Show all {option.creators.length.toLocaleString()}
        </Button>
      )}
      {option.count > option.creators.length && (
        <p className="pt-1 text-xs text-muted-foreground">
          {(option.count - option.creators.length).toLocaleString()} more weren't kept by the server, so they'd need a new search.
        </p>
      )}
    </div>
  );
}

